import { TRPCClientError } from '@trpc/client';
import { FEATURE_DETAILS, requiredPlanFor, type PlanFeature, type PlanId } from '@keel/core';
import { create } from 'zustand';

export interface PlanNeed { readonly feature: string; readonly requiredPlan: PlanId; readonly message: string }

/** `{ code: 'PLAN_LIMIT', feature, requiredPlan }` from a FORBIDDEN tRPC error (server `requirePlan`), else null. */
export const planLimitOf = (err: unknown): PlanNeed | null => {
  if (!(err instanceof TRPCClientError)) return null;
  const d = (err.data as { details?: { code?: unknown; feature?: unknown; requiredPlan?: unknown } } | undefined)?.details;
  if (d?.code !== 'PLAN_LIMIT' || typeof d.requiredPlan !== 'string') return null;
  return { feature: String(d.feature ?? ''), requiredPlan: d.requiredPlan as PlanId, message: err.message };
};

/** True for "online payments aren't set up on this server" (the UI says "Contact us to upgrade"). */
export const isBillingNotConfigured = (err: unknown): boolean =>
  err instanceof TRPCClientError && (err.data as { details?: { code?: unknown } } | undefined)?.details?.code === 'BILLING_NOT_CONFIGURED';

/** The upgrade sheet: opened by any PLAN_LIMIT error, so a gated action is never a dead end. */
export const useUpgrade = create<{ need: PlanNeed | null; open: (n: PlanNeed) => void; close: () => void }>()((set) => ({
  need: null, open: (need) => set({ need }), close: () => set({ need: null }),
}));

/** Open the upgrade sheet for a feature someone clicked on (not one the server refused). */
export const showFeature = (feature: PlanFeature): void =>
  useUpgrade.getState().open({ feature, requiredPlan: requiredPlanFor(feature), message: FEATURE_DETAILS[feature] });

